import type { MentionRow } from './generator';

/**
 * The metric registry.
 *
 * **`reduce` takes the whole row set, never a per-row fold.** This is the single
 * most important shape in the file. `nps` is a mean and `positiveShare` a ratio,
 * and a running accumulator that adds one row at a time would produce a
 * mean-of-means as soon as the rows were bucketed — each bucket's average
 * averaged again, weighting a quiet Tuesday the same as a busy Monday. Handing
 * the reducer the whole array makes that mistake impossible to write.
 *
 * `goodDirection` is why a badge can be red on a rising number: negative share
 * going up is bad, so tone comes from the metric's own direction rather than
 * from the sign of the delta.
 */

export type MetricId =
  | 'mentions'
  | 'reach'
  | 'engagement'
  | 'engagementRate'
  | 'nps'
  | 'positiveShare'
  | 'negativeShare'
  | 'negativeMentions'
  | 'positiveMentions'
  | 'netSentiment'
  | 'meanSentiment'
  | 'npsNet'
  | 'shares'
  | 'comments'
  | 'reactions'
  | 'likes';

/**
 * The end of the NPS scale, in both directions. The metric is scaled to it and
 * the card's bar is bounded by it, so the two can never disagree about what a
 * full bar means. See `npsNet` below.
 */
export const NPS_BOUND = 10;

export type MetricDef = {
  id: MetricId;
  label: string;
  /** Reduces a whole row set to one figure. Never called per row. */
  reduce: (rows: readonly MentionRow[]) => number;
  /** The figure as the UI shows it. */
  format: (value: number) => string;
  /** Which way is an improvement. Decides KPI badge tone, not the raw sign. */
  goodDirection: 'up' | 'down';
  /**
   * How a change against the comparison period is expressed.
   *
   * `percent` is the default and is right for a count or a positive rate.
   * `points` is **required** for any metric that can cross zero: a percentage
   * change from -11 to +27 is -345%, a number that is arithmetically correct
   * and completely meaningless. NPS and net sentiment are both signed, so both
   * report the gap in points instead.
   */
  deltaMode?: 'percent' | 'points';
};

const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

const sum = (rows: readonly MentionRow[], pick: (row: MentionRow) => number) => {
  let total = 0;
  for (const row of rows) total += pick(row);
  return total;
};

/** Share of rows matching a predicate, as a percentage. Zero rows → 0, never NaN. */
const shareOf = (rows: readonly MentionRow[], matches: (row: MentionRow) => boolean) => {
  if (rows.length === 0) return 0;
  let hits = 0;
  for (const row of rows) if (matches(row)) hits += 1;
  return (hits / rows.length) * 100;
};

export const metrics: Readonly<Record<MetricId, MetricDef>> = {
  mentions: {
    id: 'mentions',
    label: 'Mentions',
    /* The row count itself — which is why no row carries a `mentions: 1` field. */
    reduce: (rows) => rows.length,
    format: (value) => whole.format(value),
    goodDirection: 'up',
  },
  reach: {
    id: 'reach',
    label: 'Reach',
    reduce: (rows) => sum(rows, (row) => row.reach),
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },
  engagement: {
    id: 'engagement',
    label: 'Engagement',
    reduce: (rows) => sum(rows, (row) => row.engagement),
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },
  engagementRate: {
    id: 'engagementRate',
    label: 'Engagement rate',
    /*
     * Ratio of totals, not a mean of per-row ratios: one viral post with a
     * freakish rate would otherwise drag the average somewhere no reader would
     * recognise. Guarded against a zero denominator.
     */
    reduce: (rows) => {
      const reach = sum(rows, (row) => row.reach);
      return reach === 0 ? 0 : (sum(rows, (row) => row.engagement) / reach) * 100;
    },
    format: (value) => `${value.toFixed(1)}%`,
    goodDirection: 'up',
  },
  nps: {
    id: 'nps',
    label: 'NPS',
    /* A mean. Never sum this. */
    reduce: (rows) =>
      rows.length === 0 ? 0 : sum(rows, (row) => row.npsScore) / rows.length,
    format: (value) => value.toFixed(1),
    goodDirection: 'up',
  },
  /*
   * The sentiment mix as **one score on a 0-100 scale**, where 0 is wholly
   * negative, 50 is evenly split and 100 is wholly positive.
   *
   * A weighted mean of the categorical label — positive 100, neutral 50,
   * negative 0 — because that is the only score this corpus can produce: a row
   * carries a label the generator drew from a distribution, not a per-mention
   * score (see `sentimentThreshold`, which notes the same gap). The weights are
   * the scale's own landmarks, so a corpus of nothing but neutral reads 50 and
   * not zero.
   *
   * It is `netSentiment` rescaled — `50 + net / 2` — and that is the point:
   * the same balance on the 0-100 axis this product states sentiment on, which
   * is the axis the 25/75 threshold setting cuts. Kept as its own entry rather
   * than computed at the call site so its delta badge and that badge's colour
   * come from where every other figure's do. Asked for on the `Sentiment over
   * time` card, in place of `netSentiment`, 2026-09-23.
   *
   * `points`, like its signed sibling: the figure is a position on a fixed
   * scale, and a percentage change between two positions on one says nothing.
   */
  meanSentiment: {
    id: 'meanSentiment',
    label: 'Mean sentiment score',
    reduce: (rows) => {
      if (rows.length === 0) return 0;
      let total = 0;
      for (const row of rows) {
        if (row.sentiment === 'positive') total += 100;
        else if (row.sentiment === 'neutral') total += 50;
      }
      return total / rows.length;
    },
    format: (value) => value.toFixed(1),
    goodDirection: 'up',
    deltaMode: 'points',
  },
  netSentiment: {
    id: 'netSentiment',
    label: 'Net sentiment',
    /*
     * Positive share less negative share, in points. Reduced over the whole row
     * set rather than subtracting two separately-bucketed figures, so it cannot
     * drift from the two shares printed beside it.
     */
    reduce: (rows) => {
      if (rows.length === 0) return 0;
      let positive = 0;
      let negative = 0;
      for (const row of rows) {
        if (row.sentiment === 'positive') positive += 1;
        else if (row.sentiment === 'negative') negative += 1;
      }
      return ((positive - negative) / rows.length) * 100;
    },
    format: (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}`,
    goodDirection: 'up',
    deltaMode: 'points',
  },

  /*
   * Net promoter balance, on this product's **-10..+10** scale.
   *
   * The banding is the standard one: 9-10 promote, 0-6 detract, 7-8 are passive
   * and count only in the denominator. What is *not* standard is the multiplier:
   * a textbook NPS puts promoters-minus-detractors over responses and scales it
   * by 100, giving -100..+100. This scales by 10 instead, because the score this
   * product reports is defined to sit between -10 and +10 (2026-09-16) and the
   * NPS card's bar is drawn against a fixed +-10 bound. A ratio of -1..+1 times
   * 10 can never leave that bound, so the bar can never pin.
   *
   * Everything else about the statistic is untouched — same numerator, same
   * denominator, same passive rule — so the two differ by a constant factor and
   * nothing else. Formatted to one decimal: at a tenth the range of the textbook
   * figure, rounding to whole numbers would throw away a digit that matters.
   *
   * `nps` is the average of the raw 0-10 answers, which is a different statistic
   * with a different range; both are useful and neither substitutes for the
   * other, so they are separate metrics rather than one with a flag.
   */
  npsNet: {
    id: 'npsNet',
    label: 'NPS',
    reduce: (rows) => {
      if (rows.length === 0) return 0;
      let promoters = 0;
      let detractors = 0;
      for (const row of rows) {
        if (row.npsScore >= 9) promoters += 1;
        else if (row.npsScore <= 6) detractors += 1;
      }
      return ((promoters - detractors) / rows.length) * NPS_BOUND;
    },
    format: (value) => `${value > 0 ? '+' : ''}${value.toFixed(1)}`,
    goodDirection: 'up',
    deltaMode: 'points',
  },
  positiveShare: {
    id: 'positiveShare',
    label: 'Positive share',
    reduce: (rows) => shareOf(rows, (row) => row.sentiment === 'positive'),
    format: (value) => `${value.toFixed(1)}%`,
    goodDirection: 'up',
  },
  negativeShare: {
    id: 'negativeShare',
    label: 'Negative share',
    reduce: (rows) => shareOf(rows, (row) => row.sentiment === 'negative'),
    format: (value) => `${value.toFixed(1)}%`,
    /* The one that earns `goodDirection`: rising negativity is a red badge. */
    goodDirection: 'down',
  },
  /*
   * The **count** of negative mentions, where `negativeShare` is the rate.
   *
   * A registry entry rather than a `rows.filter(...).length` at the call site,
   * so its delta badge and that badge's *colour* come from the same place every
   * other figure's do. Counted with `mentions`' own formatter and
   * `negativeShare`'s own direction: it is a volume, and more of it is worse —
   * reading the raw sign instead would paint a rising complaint count green.
   *
   * Added 2026-09-18 for Overview's `Negative conversation` card (972:20771).
   */
  negativeMentions: {
    id: 'negativeMentions',
    label: 'Negative mentions',
    reduce: (rows) => rows.filter((row) => row.sentiment === 'negative').length,
    format: (value) => compact.format(value),
    goodDirection: 'down',
  },

  /*
   * The mirror of `negativeMentions`: a count, formatted as a volume, and more
   * of it is better. Added 2026-09-21 for Media's `Positive mentions` card
   * (2123:83788), which sits between the total and the negative count.
   */
  positiveMentions: {
    id: 'positiveMentions',
    label: 'Positive mentions',
    reduce: (rows) => rows.filter((row) => row.sentiment === 'positive').length,
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },

  /*
   * `engagement` split by interaction type. The four are sums of fields the
   * generator guarantees add back to `engagement` on every row, so
   * `shares + comments + reactions + likes` reduces to exactly `engagement`
   * over any row set — a by-type chart and the engagement KPI above it cannot
   * disagree.
   *
   * Listed most valuable first, which is the order a by-type chart should plot
   * them: a share is worth more than a like and should not be buried under it.
   */
  shares: {
    id: 'shares',
    label: 'Shares',
    reduce: (rows) => sum(rows, (row) => row.shares),
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },
  comments: {
    id: 'comments',
    label: 'Comments',
    reduce: (rows) => sum(rows, (row) => row.comments),
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },
  /*
   * **Reactions include likes.** A like *is* a reaction — it is the one every
   * platform gives you by default — so the corpus' two fields are one reading,
   * and splitting them put a `Likes` bar beside a `Reactions` bar that measured
   * the same gesture at two different temperatures.
   *
   * The fold the tables have made since 2026-09-18 (`pages.ts`, `posts.ts`,
   * `quickSearch.ts`), brought into the registry on the user's instruction
   * 2026-09-23 so the charts agree with the tables. It also closes the
   * arithmetic: the corpus guarantees shares + comments + reactions + likes is
   * exactly `engagement` (see `splitEngagement`), so with likes folded in,
   * **Shares + Comments + Reactions now sums to Total engagement** — which is
   * what a stacked chart of the three is read as promising.
   *
   * **Never sum `reactions` and `likes`.** They overlap now; the total is
   * `engagement`.
   */
  reactions: {
    id: 'reactions',
    label: 'Reactions',
    reduce: (rows) => sum(rows, (row) => row.reactions + row.likes),
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },
  /*
   * Likes on their own. Nothing charts this since the fold above — it is kept
   * because the corpus field is real and a card may one day want the split, and
   * because deleting it would say the data does not exist. Anything drawing it
   * beside `reactions` is double-counting.
   */
  likes: {
    id: 'likes',
    label: 'Likes',
    reduce: (rows) => sum(rows, (row) => row.likes),
    format: (value) => compact.format(value),
    goodDirection: 'up',
  },
};
